import { GWCA } from '~/lib/ca-constants';
import { GWUtils } from '~/utils/ca-utils';

customElements.define(
  'gw-customer-accounts-sign-in',
  class GWCustomerAccountsSignIn extends HTMLElement {
    constructor() {
      super();
      this.signInType = this.getAttribute('type');
      this.signInBlock = this.querySelector('[data-sign-in-block]');
      if (this.signInType === 'password') return;

      this.verifyCodeBlock = this.querySelector('[data-verify-code-block]');
      this.tryAnotherEmailButton = this.querySelector('[data-try-another-email]');
      this.sendAnotherCodeButton = this.querySelector('[data-send-another-code]');

      this.inputtedEmail = '';
      this.inputtedCode = '';
    }

    connectedCallback() {
      if (this.signInType === 'password') {
        this._setupPasswordBasedAuth();
      } else {
        this._setupEmailBasedAuth();
      }
    }

    _setupPasswordBasedAuth() {
      // Cache DOM elements
      const forms = {
        signIn: this.querySelector('[data-sign-in-form]'),
        registration: this.querySelector('[data-registeration-form]'),
        forgotPassword: this.querySelector('[data-forgot-password-form]'),
      };

      const links = {
        signIn: this.querySelectorAll('[data-sign-in-link]'),
        createAccount: this.querySelectorAll('[data-create-account-link]'),
        forgotPassword: this.querySelectorAll('[data-forgot-password-link]'),
      };

      // Set up navigation between forms
      this._setupFormNavigation(links, forms);

      // Set up form submission for all forms
      this._setupFormSubmissions(Object.values(forms));

      // Handle form visibility based on query params
      this._handleFormVisibility(forms);
    }

    _handleFormVisibility(forms) {
      const queryParam = new URLSearchParams(window.location.search);
      const formId = queryParam.get('form_id');
      if (!formId) return;

      Object.values(forms).forEach((form) => {
        const errorBox = form.querySelector('.gw-error-message');

        if (formId === form.getAttribute('id')) {
          if (form) form.removeAttribute('hidden');
          if (errorBox) errorBox.removeAttribute('hidden');

          // Remove query params from URL
          // window.history.replaceState(
          //   {},
          //   document.title,
          //   window.location.pathname
          // );
        } else {
          if (form) form.setAttribute('hidden', '');
          if (errorBox) errorBox.setAttribute('hidden', '');
        }
      });
    }

    _setupFormNavigation(links, forms) {
      // Helper to show one form and hide others
      const showForm = (formToShow) => {
        Object.values(forms).forEach((form) => {
          form.setAttribute('hidden', '');
        });
        formToShow.removeAttribute('hidden');
      };

      // Add event listeners to navigation links
      links.createAccount.forEach((link) => {
        link.addEventListener('click', () => showForm(forms.registration));
      });

      links.forgotPassword.forEach((link) => {
        link.addEventListener('click', () => showForm(forms.forgotPassword));
      });

      links.signIn.forEach((link) => {
        link.addEventListener('click', () => showForm(forms.signIn));
      });
    }

    _setupFormSubmissions(formList) {
      formList.forEach((form) => {
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.append('my-account-open', true);
        returnUrl.searchParams.append('form_id', form.getAttribute('id'));

        // Update return_to input value
        const returnToInput = form.querySelector('input[name="return_to"]');
        const returnUrlInput = form.querySelector('input[name="return_url"]');
        if (returnToInput) returnToInput.value = returnUrl.pathname + returnUrl.search;
        if (returnUrlInput) returnUrlInput.value = returnUrl.pathname + returnUrl.search;

        // Handle form submission with captcha
        form.addEventListener('submit', (event) => {
          event.preventDefault();

          // Disable form submission button and show loader
          form.querySelectorAll('button[type="submit"]').forEach((button) => {
            button.disabled = true;

            const loader = button.querySelector('[data-loader]');
            if (loader) loader.hidden = false;
          });

          Shopify.captcha.protect(form, () => {
            form.submit();
          });
        });
      });
    }

    _setupEmailBasedAuth() {
      this._handleEmailSubmitForm();
      this._handleInputCodeGroup();

      // Handle "Try Another Email" button
      this.tryAnotherEmailButton.addEventListener('click', () => {
        this._resetToEmailInput();
      });

      // Handle "Send Another Code" button
      this.sendAnotherCodeButton.addEventListener('click', () => {
        this._resendVerificationCode();
      });
    }

    _resetToEmailInput() {
      this.signInBlock.removeAttribute('hidden');
      this.verifyCodeBlock.setAttribute('hidden', '');

      const emailInput = this.signInBlock.querySelector('sl-input');
      emailInput.value = '';
      emailInput.focus();

      this.inputtedEmail = '';
      this.inputtedCode = '';
      this.acceptsMarketing = false;
    }

    _resendVerificationCode() {
      this._submitEmailForm(this.querySelector('[data-sign-in-form]'));
      GWUtils.notify('Another code has been sent to your email.');
    }

    /**
     * Handles the email submit form.
     * @returns {void}
     */
    async _handleEmailSubmitForm() {
      await Promise.all([customElements.whenDefined('sl-input')]).then(() => {
        const emailSubmitForm = this.querySelector('[data-sign-in-form]');
        const emailErrorBox = this.querySelector('[data-email-error]');

        emailSubmitForm.addEventListener(
          'sl-invalid',
          (event) => {
            event.preventDefault();

            emailErrorBox.textContent = event.target.validationMessage;
            emailErrorBox.hidden = false;

            event.target.focus();
          },
          { capture: true }
        );

        emailSubmitForm.addEventListener('submit', (event) => {
          event.preventDefault();

          emailErrorBox.hidden = true;
          emailErrorBox.textContent = '';

          this._submitEmailForm(emailSubmitForm).then(() => {
            // Focus on the first input in the next block
            const firstInputInNextBlock = this.verifyCodeBlock.querySelector('.code-input');
            if (firstInputInNextBlock) firstInputInNextBlock.focus();
          });
        });
      });
    }

    /**
     * Submits the email form to request a verification code.
     * @param {HTMLFormElement} form - The email form element.
     * @returns {void}
     */
    async _submitEmailForm(form) {
      const submitButton = form.querySelector('button[type="submit"]');
      submitButton.disabled = true;
      submitButton.querySelector('[data-loader]').hidden = false;

      const formData = new FormData(form);
      this.inputtedEmail = formData.get('email');
      if (formData.has('accepts_marketing')) {
        this.acceptsMarketing = formData.get('accepts_marketing');
      }

      // Fetch hidden captcha token
      let token = '';
      const captchaToken = await this._fetchCaptchaToken();
      if (captchaToken) {
        token = captchaToken;
      }

      try {
        const response = await this._request(
          { customerEmail: this.inputtedEmail, token: token },
          GWCA.ENDPOINTS.AUTH.SIGN_IN
        );

        if (response.error) {
          console.error(response.error);
          return;
        }

        const $codeSentToBlock = this.querySelector('[data-code-sent-to]');
        if ($codeSentToBlock) $codeSentToBlock.textContent = this.inputtedEmail;

        this.signInBlock.setAttribute('hidden', '');
        this.verifyCodeBlock.removeAttribute('hidden');

        return response;
      } catch (error) {
        console.error(error);
        alert('An error occurred. Please try again later.');
      } finally {
        submitButton.disabled = false;
        submitButton.querySelector('[data-loader]').hidden = true;
      }
    }

    /**
     * Submits the verification code to sign in the customer.
     * @returns {void}
     * @async
     */
    async _submitVerificationCode() {
      const verifyActionButtonsWrapper = this.querySelector('.gw-verify-code__actions');
      const verifyActionsButtons = verifyActionButtonsWrapper.querySelectorAll('button, a');
      const verifyErrorBox = verifyActionButtonsWrapper.querySelector('[data-verify-error]');
      const verifyLoader = verifyActionButtonsWrapper.querySelector('[data-loader]');
      const verifyInputs = this.querySelectorAll('input.code-input');

      try {
        verifyInputs.forEach((input) => {
          input.disabled = true;
        });

        verifyActionsButtons.forEach((button) => {
          button.disabled = true;
          button.hidden = true;
        });

        verifyErrorBox.hidden = true;
        verifyLoader.hidden = false;

        const verificationResponse = await this._request(
          {
            customerEmail: this.inputtedEmail,
            code: this.inputtedCode,
            is_marketing: this.acceptsMarketing,
          },
          GWCA.ENDPOINTS.AUTH.VERIFY_CODE
        );

        if (!verificationResponse.url) {
          throw new Error('Verification failed');
        }

        // Save the token to session storage
        localStorage.setItem(GWCA.STORAGE_KEYS.TOKEN, verificationResponse.token);

        // Save the email to session storage
        localStorage.setItem(GWCA.STORAGE_KEYS.EMAIL, this.inputtedEmail);

        const signinResponse = await fetch(verificationResponse.url);

        if (signinResponse.error) {
          throw new Error(signinResponse.error);
        }

        const updatedSiteResponse = await fetch(window.location.href);
        const updatedSiteText = await updatedSiteResponse.text();
        const updatedSiteDOM = new DOMParser().parseFromString(updatedSiteText, 'text/html');
        const loyaltyPath = updatedSiteDOM
          .querySelector('#gw-customer-accounts-template')
          .content?.querySelector('rivo-points')
          ?.getAttribute('page-url');

        // Update the site with the new content
        document.documentElement.innerHTML = updatedSiteDOM.documentElement.innerHTML;

        document.dispatchEvent(new CustomEvent('gw-customer-accounts:login'));

        // Redirect if there is a session storage redirect URL
        const redirectURL = sessionStorage.getItem(GWCA.STORAGE_KEYS.REDIRECT_URL);
        if (redirectURL) {
          sessionStorage.removeItem(GWCA.STORAGE_KEYS.REDIRECT_URL);
          window.location.href = redirectURL;
        }

        // If we are on a one of the loyalty pages, reload the page to show the customer's points
        if (!loyaltyPath) return;

        const currentPath = window.location.pathname;

        if (currentPath.includes(loyaltyPath)) {
          window.location.reload();
        }
      } catch (error) {
        console.error(error);

        setTimeout(() => {
          // Change focus to the last input box
          verifyInputs[verifyInputs.length - 1].focus();
        });
      } finally {
        verifyInputs.forEach((input) => {
          input.disabled = false;
        });

        verifyActionsButtons.forEach((button) => {
          button.disabled = false;
          button.hidden = false;
        });

        verifyErrorBox.hidden = false;
        verifyLoader.hidden = true;
      }
    }

    /**
     * Makes a request to the customer accounts API.
     * @param {Object} body - The request body.
     * @param {string} url - The API endpoint URL.
     * @param {string} [method='POST'] - The request method.
     * @returns {Promise<Object>} - The response data.
     * @async
     */
    async _request(body, url, method = 'POST') {
      const response = await fetch(GWUtils.baseURL + url, {
        method: method,
        body: JSON.stringify(body),
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    }

    /**
     * Handles the input code group for the verification code.
     * @returns {void}
     */
    _handleInputCodeGroup() {
      const inputElements = [...this.querySelectorAll('input.code-input')];

      inputElements.forEach((ele, index) => {
        ele.addEventListener('keydown', (e) => {
          // Allow Ctrl + V and Cmd + V for pasting
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 86) {
            return;
          }

          // Allow only numbers
          if (e.keyCode !== 8 && (e.keyCode < 48 || e.keyCode > 57)) {
            e.preventDefault();
          }

          // Focus on the previous input box if backspace is pressed and the current input box is empty
          if (e.keyCode === 8 && e.target.value === '') {
            inputElements[Math.max(0, index - 1)].focus();
          }
        });

        ele.addEventListener('input', (e) => {
          const [first, ...rest] = e.target.value;
          e.target.value = first ?? '';

          const lastInputBox = index === inputElements.length - 1;
          const didInsertContent = first !== undefined;

          if (didInsertContent && !lastInputBox) {
            inputElements[index + 1].focus();
            inputElements[index + 1].value = rest.join('');
            inputElements[index + 1].dispatchEvent(new Event('input'));
          }

          // if last input box entered and all boxes are filled, submit the form
          if (didInsertContent && lastInputBox && inputElements.every((input) => input.value)) {
            this.inputtedCode = inputElements.map((input) => input.value).join('');

            this._submitVerificationCode();
          }
        });
      });
    }

    /**
     * Fetches the captcha token.
     * @returns {Promise<string>}
     */
    async _fetchCaptchaToken() {
      const response = await fetch(GWUtils.baseURL + GWCA.ENDPOINTS.AUTH.GET_CAPTCHA_TOKEN);
      const data = await response.json();
      return data?.token;
    }
  }
);
